import { useMemo } from 'react';

/*
 * Is this a company's co-branded advisor, and if so, which one?
 *
 * Two ways in, because they answer different needs:
 *
 *   acme.aibrokerhub.com   the branded address a company puts on its website
 *   /a/{slug}              a specific link, with its own attribution
 *
 * Neither is trusted. The client sends what it observed — a hostname label or a
 * slug — and the SERVER decides which company that means. A company id computed
 * here would be a value the browser could edit, and editing it would read
 * another developer's private inventory.
 *
 * So this returns only "there is an advisor context, and here is the raw
 * identifier". Everything else about the company (its name, its logo, what it
 * can retrieve) comes back from the server in the chat response.
 */

// Hosts that are the product itself rather than a company.
const RESERVED_HOSTS = new Set([
  'www', 'chat', 'app', 'api', 'chatapi', 'indexingapi', 'indexingtool',
  'admin', 'staging', 'dev', 'test', 'preview', 'localhost',
]);

function labelFromHost(hostname) {
  const host = (hostname || '').toLowerCase().split(':')[0];
  const parts = host.split('.').filter(Boolean);

  // A bare host (localhost) or an apex domain has no company label.
  if (parts.length < 3) return null;

  const label = parts[0];
  if (RESERVED_HOSTS.has(label)) return null;
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(label)) return null;
  return label;
}

export default function useAdvisorContext(pathname = window.location.pathname) {
  return useMemo(() => {
    // A path slug wins over the hostname: it names a specific link, and the
    // hostname only names the company. Following acme.example.com/a/xyz should
    // attribute the lead to link xyz.
    const match = pathname.match(/^\/a\/([A-Za-z0-9]{10,64})\/?$/);
    if (match) {
      return { isAdvisor: true, slug: match[1], source: 'path' };
    }

    const label = labelFromHost(window.location.hostname);
    if (label) {
      // No slug: the server resolves the company from the Host header it
      // received, which is the one thing here the browser cannot forge.
      return { isAdvisor: true, slug: null, source: 'subdomain', label };
    }

    return { isAdvisor: false, slug: null, source: null };
  }, [pathname]);
}
